export function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// axis-aligned bbox overlap (uses unrotated element box)
export function intersects(a, b) {
  return (
    a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y
  );
}

export function dist(p1, p2) {
  return Math.hypot(p2.x - p1.x, p2.y - p1.y);
}

// total length (meters) of a fence polyline given points in world px
export function fenceLengthM(points, ppm) {
  if (!points || points.length < 2 || !ppm) return 0;
  let px = 0;
  for (let i = 1; i < points.length; i++) {
    px += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return px / ppm;
}

// Bounding box (in world px) around an element plus its nearest `count`
// neighbours, with padding. Used to zoom the view onto a local cluster.
export function neighbourBBox(elements, id, ppm, count = 6) {
  const el = elements.find((e) => e.id === id);
  if (!el) return null;
  const rectOf = (e) => {
    if (e.kind === "fence" && e.points && e.points.length) {
      let mnx = Infinity, mny = Infinity, mxx = -Infinity, mxy = -Infinity;
      e.points.forEach((p) => {
        mnx = Math.min(mnx, p.x); mny = Math.min(mny, p.y);
        mxx = Math.max(mxx, p.x); mxy = Math.max(mxy, p.y);
      });
      return { minX: mnx, minY: mny, maxX: mxx, maxY: mxy, cx: (mnx + mxx) / 2, cy: (mny + mxy) / 2 };
    }
    const w = (Number(e.length) || 0) * ppm;
    const h = (Number(e.width) || 0) * ppm;
    return { minX: e.x, minY: e.y, maxX: e.x + w, maxY: e.y + h, cx: e.x + w / 2, cy: e.y + h / 2 };
  };
  const base = rectOf(el);
  const near = elements
    .filter((e) => e.id !== id)
    .map((e) => rectOf(e))
    .sort((a, b) => (a.cx - base.cx) ** 2 + (a.cy - base.cy) ** 2 - ((b.cx - base.cx) ** 2 + (b.cy - base.cy) ** 2))
    .slice(0, count);
  const all = [base, ...near];
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  all.forEach((r) => {
    minX = Math.min(minX, r.minX); minY = Math.min(minY, r.minY);
    maxX = Math.max(maxX, r.maxX); maxY = Math.max(maxY, r.maxY);
  });
  const padX = (maxX - minX) * 0.18 + 12;
  const padY = (maxY - minY) * 0.18 + 12;
  return {
    minX: minX - padX, minY: minY - padY, maxX: maxX + padX, maxY: maxY + padY,
    cx: base.cx, cy: base.cy, // selected element center (used to keep it centered)
  };
}

export const PLACEHOLDER_BG =
  "https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxibHVlcHJpbnQlMjBmbG9vciUyMHBsYW58ZW58MHx8fHwxNzgxNzgxNjU5fDA&ixlib=rb-4.1.0&q=85&w=1600";

export function toCsv(headers, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const lines = [headers.join(",")];
  for (const r of rows) lines.push(headers.map((h) => esc(r[h])).join(","));
  return lines.join("\n");
}

export function parseCsv(text) {
  if (text && text.charCodeAt(0) === 0xFEFF) text = text.slice(1); // strip BOM
  // auto-detect the delimiter from the header line (Excel RO uses ';')
  const firstLine = (text.split(/\r?\n/)[0]) || "";
  const counts = { ",": 0, ";": 0, "\t": 0 };
  let q0 = false;
  for (const ch of firstLine) {
    if (ch === '"') q0 = !q0;
    else if (!q0 && counts[ch] !== undefined) counts[ch]++;
  }
  const delim = [",", ";", "\t"].reduce((a, b) => (counts[b] > counts[a] ? b : a), ",");

  const rows = [];
  let i = 0, field = "", row = [], inQ = false;
  const pushF = () => { row.push(field); field = ""; };
  const pushR = () => { rows.push(row); row = []; };
  while (i < text.length) {
    const c = text[i];
    if (inQ) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQ = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQ = true; i++; continue; }
    if (c === delim) { pushF(); i++; continue; }
    if (c === "\r") { i++; continue; }
    if (c === "\n") { pushF(); pushR(); i++; continue; }
    field += c; i++;
  }
  if (field.length || row.length) { pushF(); pushR(); }
  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1)
    .filter((r) => r.some((c) => c !== ""))
    .map((r) => {
      const o = {};
      headers.forEach((h, idx) => { o[h] = r[idx]; });
      return o;
    });
}
